import type { Skill } from '../../types';
import { getSetting, setSetting } from '../db';
import { NEW_SHARE } from '../fsrs';

// What a setup screen chooses before a round starts, and the memory of what it
// chose last time.
//
// Every mode keeps its own settings. Twenty name cards is a couple of minutes
// and twenty recordings is a sitting, so the learner who wants a long round of
// one rarely wants a long round of the other; a single shared length would have
// them re-picking it on every switch. The same goes for how much of a round is
// spent meeting birds rather than revising them.

/** Round lengths a setup screen offers, shortest first. */
export const ROUND_LENGTHS = [5, 10, 15, 20];

/** The round every mode ran before the length could be chosen. */
export const DEFAULT_ROUND_LEN = 10;

/**
 * What a remembered round length belongs to.
 *
 * The three skills, plus the two modes that are not skills and never will be —
 * the Field Test, which grades all three skills' own cards, and Sound Match,
 * which grades the sound card from the other side. Both have a setup screen,
 * and a round of twenty of either is a different sitting from twenty of any one
 * skill, so their lengths are their own for the same reason the skills' are.
 */
export type RoundKind = Skill | 'field' | 'match';

function lengthKey(kind: RoundKind): string {
  return `${kind}.length`;
}

/**
 * How many birds this mode was last quizzed on — the default for its next
 * round. An unset or since-retired value falls back rather than being trusted:
 * settings ride along in a backup and outlive the list they were picked from.
 */
export async function roundLength(kind: RoundKind): Promise<number> {
  const saved = await getSetting<number>(lengthKey(kind));
  return ROUND_LENGTHS.includes(saved as number) ? (saved as number) : DEFAULT_ROUND_LEN;
}

export async function setRoundLength(kind: RoundKind, len: number): Promise<void> {
  await setSetting(lengthKey(kind), len);
}

/**
 * How much of a round is held for birds the learner has never met, as a share
 * of its length — the choices a setup screen offers, fewest first.
 *
 * A share rather than a count, so that it survives the length beside it
 * changing: three of ten and six of twenty are the same appetite for strangers,
 * and a learner who lengthens a round is asking for more of the same round, not
 * for a suddenly heavier one. `NEW_SHARE` is among them because a list of
 * options that cannot express the default is a list that silently changes it.
 */
export const NEW_SHARES = [0, NEW_SHARE, 0.6, 1];

function newKey(kind: RoundKind): string {
  return `${kind}.new`;
}

/**
 * The share of the next round this mode holds for unmet species. Per mode for
 * the same reason the length is: meeting six new birds by their songs is a
 * different afternoon from meeting six by their names, and the learner who
 * wants to push on in one is often the one consolidating in the other.
 *
 * Validated against the list on offer rather than trusted, exactly as the
 * length is — a stored 0.45 from some future version would otherwise deal a
 * round nothing on the screen claims.
 */
export async function newBirdShare(kind: RoundKind): Promise<number> {
  const saved = await getSetting<number>(newKey(kind));
  return NEW_SHARES.includes(saved as number) ? (saved as number) : NEW_SHARE;
}

export async function setNewBirdShare(kind: RoundKind, share: number): Promise<void> {
  await setSetting(newKey(kind), share);
}

const K_CALLS = 'sound.calls';

/**
 * Whether Sound ID puts calls in the mix.
 *
 * On, a round plays whatever recordings a bird has — songs, alarms and the
 * calls too, in the proportion the pack holds them. Off, calls are left out and
 * the round is the songs and alarms a bird is usually learned by. The key name
 * predates the flag reading this way round and is kept so that a learner who
 * has already chosen keeps their choice.
 *
 * `unlocked` is `soundHardUnlocked()`'s verdict, and it is only the DEFAULT:
 * clearing the mastery bar turns calls on for a learner who has never touched
 * the setting, and does nothing else. Below the bar they can still opt in — the
 * mode is harder, not off-limits, and someone who wants it is exactly the
 * person it is for — and above it they can opt back out, which matters more:
 * the unlock used to be a one-way door that took the songs away for good.
 */
export async function soundWithCalls(unlocked: boolean): Promise<boolean> {
  const saved = await getSetting<boolean>(K_CALLS);
  return typeof saved === 'boolean' ? saved : unlocked;
}

export async function setSoundWithCalls(on: boolean): Promise<void> {
  await setSetting(K_CALLS, on);
}

const K_RETRY = 'retry.misses';

/**
 * Whether a missed question comes back at the end of the round, and keeps
 * coming back until it is answered right.
 *
 * One switch for every mode rather than one per mode: it is a way of studying,
 * not a property of a skill, and a learner who wants to leave no miss standing
 * wants that of sounds and names alike. Off by default — a round that can grow
 * is a round whose length no longer means what the setup screen said.
 *
 * Only the first answer is graded. The comeback is practice: the schedule has
 * already heard "Again", and telling it "Good" two minutes later would be the
 * learner's short-term memory talking, not recall.
 */
export async function retryMisses(): Promise<boolean> {
  return (await getSetting<boolean>(K_RETRY)) === true;
}

export async function setRetryMisses(on: boolean): Promise<void> {
  await setSetting(K_RETRY, on);
}
